import { createSelector } from 'reselect'
import { ShiftPostStep } from '@features/shift-post-v2/steps'
import { AppState } from 'store/types'

/**
 * @param state - the application state
 * @returns shiftPostV2 - the shift post v2 reducer
 */
export const selectShiftPostV2 = (state: AppState) => state.shiftPostV2

/**
 * Gets all steps from the shift post v2 state
 */
export const selectShiftPostV2Steps = createSelector(
  selectShiftPostV2,
  state => state.steps,
)

/**
 * Gets the loading state
 */
export const selectShiftPostV2Loading = createSelector(
  selectShiftPostV2,
  state => state.isLoading,
)

/**
 * Gets the error state
 */
export const selectShiftPostV2Error = createSelector(
  selectShiftPostV2,
  state => state.error,
)

// Step-specific selectors
/**
 * Gets the shift details step
 */
export const selectShiftDetailsStep = createSelector(
  selectShiftPostV2Steps,
  steps => steps[ShiftPostStep.SHIFT_DETAILS],
)

/**
 * Gets the shift details data
 */
export const selectShiftDetailsData = createSelector(
  selectShiftDetailsStep,
  step => step.data,
)

/**
 * Gets the shift details confirmed status
 */
export const selectShiftDetailsConfirmed = createSelector(
  selectShiftDetailsStep,
  step => step.confirmed,
)

/**
 * Gets the acceptance criteria step
 */
export const selectAcceptanceCriteriaStep = createSelector(
  selectShiftPostV2Steps,
  steps => steps[ShiftPostStep.ACCEPTANCE_CRITERIA],
)

/**
 * Gets the acceptance criteria data
 */
export const selectAcceptanceCriteriaData = createSelector(
  selectAcceptanceCriteriaStep,
  step => step.data,
)

/**
 * Gets the acceptance criteria confirmed status
 */
export const selectAcceptanceCriteriaConfirmed = createSelector(
  selectAcceptanceCriteriaStep,
  step => step.confirmed,
)

/**
 * Gets the screening requirements step
 */
export const selectScreeningRequirementsStep = createSelector(
  selectShiftPostV2Steps,
  steps => steps[ShiftPostStep.SCREENING_REQUIREMENTS],
)

/**
 * Gets the screening requirements data
 */
export const selectScreeningRequirementsData = createSelector(
  selectScreeningRequirementsStep,
  step => step.data,
)

/**
 * Gets the screening requirements confirmed status
 */
export const selectScreeningRequirementsConfirmed = createSelector(
  selectScreeningRequirementsStep,
  step => step.confirmed,
)

/**
 * Gets the invite upshifters step
 */
export const selectInviteUpshiftersStep = createSelector(
  selectShiftPostV2Steps,
  steps => steps[ShiftPostStep.INVITE_UPSHIFTERS],
)

/**
 * Gets the invite upshifters data
 */
export const selectInviteUpshiftersData = createSelector(
  selectInviteUpshiftersStep,
  step => step.data,
)

/**
 * Gets the invite upshifters confirmed status
 */
export const selectInviteUpshiftersConfirmed = createSelector(
  selectInviteUpshiftersStep,
  step => step.confirmed,
)

/**
 * Gets the confirm step
 */
export const selectConfirmStep = createSelector(
  selectShiftPostV2Steps,
  steps => steps[ShiftPostStep.CONFIRM],
)

/**
 * Gets the confirm data
 */
export const selectConfirmData = createSelector(
  selectConfirmStep,
  step => step.data,
)

/**
 * Gets the confirm confirmed status
 */
export const selectConfirmConfirmed = createSelector(
  selectConfirmStep,
  step => step.confirmed,
)

/**
 * Gets all the collected data from all steps
 */
export const selectAllStepsData = createSelector(
  selectShiftPostV2Steps,
  steps => ({
    [ShiftPostStep.SHIFT_DETAILS]: steps[ShiftPostStep.SHIFT_DETAILS].data,
    [ShiftPostStep.ACCEPTANCE_CRITERIA]:
      steps[ShiftPostStep.ACCEPTANCE_CRITERIA].data,
    [ShiftPostStep.SCREENING_REQUIREMENTS]:
      steps[ShiftPostStep.SCREENING_REQUIREMENTS].data,
    [ShiftPostStep.INVITE_UPSHIFTERS]:
      steps[ShiftPostStep.INVITE_UPSHIFTERS].data,
    [ShiftPostStep.CONFIRM]: steps[ShiftPostStep.CONFIRM].data,
  }),
)

/**
 * Gets the post shift initial values
 */
export const selectShiftPostV2InitialValues = createSelector(
  selectShiftPostV2,
  state => state.initialValues,
)

/**
 * Gets the edit shift initial values
 */
export const selectShiftPostV2CopyEditInitialValues = createSelector(
  selectShiftPostV2,
  state => state.copyEditInitialValues,
)

/**
 * Gets the edit shift status for request
 */
export const selectShiftPostV2EditStatusForRequest = createSelector(
  selectShiftPostV2,
  state => state.copyEditStatusForRequest,
)

/**
 * Gets the rosters
 */
export const selectRosters = createSelector(
  selectShiftPostV2,
  state => state.rosterData.data,
)

/**
 * Gets the rosters fetching state
 */
export const selectRostersIsFetching = createSelector(
  selectShiftPostV2,
  state => state.rosterData.isFetching,
)

/**
 * Gets the invites history
 */
export const selectInvitesHistory = createSelector(
  selectShiftPostV2,
  state => state.invitesHistoryData.data,
)

/**
 * Gets the invites history fetching state
 */
export const selectInvitesHistoryIsFetching = createSelector(
  selectShiftPostV2,
  state => state.invitesHistoryData.isFetching,
)

/**
 * Gets the invites history error state
 */
export const selectInvitesHistoryError = createSelector(
  selectShiftPostV2,
  state => state.invitesHistoryData.error,
)
